"use client";

import { ImagePreviewDialog } from "@cmp-src/data-display/ImagePreviewDialog";
import { AttachmentTile } from "@cmp-src/upload/AttachmentTile";
import type { WorkplaceRequestCommentAttachment } from "@gettactic/api";
import { isImageFile } from "@gettactic/helpers/src/files/fileType";
import { useMemo, useState } from "react";

interface CommentAttachmentsListProps {
  attachments: WorkplaceRequestCommentAttachment[];
}

/**
 * The files posted with a comment. Images come first as thumbnails that open in
 * a pageable lightbox; everything else follows as chips that download on click.
 */
export const CommentAttachmentsList: React.FC<CommentAttachmentsListProps> = ({
  attachments
}) => {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const { images, documents } = useMemo(() => {
    const imageList: WorkplaceRequestCommentAttachment[] = [];
    const documentList: WorkplaceRequestCommentAttachment[] = [];
    for (const attachment of attachments) {
      if (isImageFile(attachment.filename, attachment.url)) {
        imageList.push(attachment);
      } else {
        documentList.push(attachment);
      }
    }
    return { images: imageList, documents: documentList };
  }, [attachments]);

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-col gap-2">
      {images.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {images.map((attachment, index) => (
            <AttachmentTile
              key={attachment.url}
              filename={attachment.filename}
              url={attachment.url}
              size="md"
              onOpen={() => setPreviewIndex(index)}
            />
          ))}
        </div>
      ) : null}

      {documents.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {documents.map((attachment) => (
            <AttachmentTile
              key={attachment.url}
              filename={attachment.filename}
              url={attachment.url}
            />
          ))}
        </div>
      ) : null}

      <ImagePreviewDialog
        images={images.map((attachment) => ({
          url: attachment.url,
          filename: attachment.filename
        }))}
        index={previewIndex}
        onIndexChange={setPreviewIndex}
        onClose={() => setPreviewIndex(null)}
      />
    </div>
  );
};
